import { useCallback, useEffect, useRef, useState } from 'react';

type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((ev: SpeechRecognitionEventLike) => void) | null;
  onerror: ((ev: { error?: string }) => void) | null;
  onend: (() => void) | null;
};

type SpeechRecognitionEventLike = {
  resultIndex: number;
  results: ArrayLike<{
    isFinal: boolean;
    0: { transcript: string };
  }>;
};

function getSpeechRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  if (typeof window === 'undefined') return null;
  // فقط در context امن (https / localhost)
  if (!window.isSecureContext) return null;
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** پیش‌نمایش زنده (اختیاری) — نتیجه اصلی از Whisper است */
export function useLiveSpeech(lang = 'fa-IR') {
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const [finalText, setFinalText] = useState('');
  const [interimText, setInterimText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const shouldListenRef = useRef(false);
  const finalTextRef = useRef('');

  useEffect(() => {
    setSupported(Boolean(getSpeechRecognitionCtor()));
  }, []);

  const stop = useCallback(() => {
    shouldListenRef.current = false;
    try {
      recognitionRef.current?.stop();
    } catch {
      /* ignore */
    }
    setListening(false);
  }, []);

  const reset = useCallback(() => {
    finalTextRef.current = '';
    setFinalText('');
    setInterimText('');
    setError(null);
  }, []);

  const start = useCallback(async () => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      // پشتیبانی ندارد — ساکت؛ Whisper کار را انجام می‌دهد
      setSupported(false);
      return;
    }

    setError(null);
    shouldListenRef.current = true;

    // اجازه میکروفون قبل از SpeechRecognition
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
    } catch {
      setError('دسترسی میکروفون داده نشد.');
      shouldListenRef.current = false;
      return;
    }

    try {
      recognitionRef.current?.abort();
    } catch {
      /* ignore */
    }

    const tryLangs = [lang, 'fa', 'fa-IR'];
    const recognition = new Ctor();
    recognition.lang = tryLangs[0];
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognitionRef.current = recognition;

    recognition.onresult = (event) => {
      let interim = '';
      let newlyFinal = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const piece = result[0]?.transcript ?? '';
        if (result.isFinal) newlyFinal += piece;
        else interim += piece;
      }
      if (newlyFinal) {
        const next = `${finalTextRef.current} ${newlyFinal}`.replace(/\s+/g, ' ').trim();
        finalTextRef.current = next;
        setFinalText(next);
      }
      setInterimText(interim.trim());
    };

    recognition.onerror = (ev) => {
      const code = ev.error ?? '';
      if (code === 'aborted' || code === 'no-speech') return;
      if (code === 'not-allowed') {
        setError('دسترسی میکروفون داده نشد.');
        shouldListenRef.current = false;
        setListening(false);
        return;
      }
      // خطاهای شبکه/زبان را بی‌صدا رد کن؛ Whisper جایگزین است
      if (code === 'language-not-supported' || code === 'network' || code === 'service-not-allowed') {
        shouldListenRef.current = false;
        setListening(false);
        setSupported(false);
        return;
      }
      setError(`خطای تشخیص زنده: ${code}`);
    };

    recognition.onend = () => {
      if (shouldListenRef.current) {
        try {
          recognition.start();
          return;
        } catch {
          shouldListenRef.current = false;
        }
      }
      setListening(false);
    };

    try {
      recognition.start();
      setListening(true);
    } catch {
      shouldListenRef.current = false;
      setListening(false);
      setSupported(false);
    }
  }, [lang]);

  useEffect(
    () => () => {
      shouldListenRef.current = false;
      try {
        recognitionRef.current?.abort();
      } catch {
        /* ignore */
      }
    },
    []
  );

  const liveText = [finalText, interimText].filter(Boolean).join(' ').trim();

  return {
    supported,
    listening,
    finalText,
    interimText,
    liveText,
    error,
    start,
    stop,
    reset,
    setFinalText: (text: string) => {
      finalTextRef.current = text;
      setFinalText(text);
    },
  };
}
